// DAC7 legal-form and field labels, per market. The backend hands over the
// field keys and validation rules (see `Dac7BusinessDetails.vue`), but not
// their local names, so the frontend owns this map. Sourced from the same
// local names the backend's own label_key config uses - see
// config/locales/global.yml's `dac7` section.
export const dac7Labels = {
  nl: {
    legalForms: {
      bv: 'Besloten vennootschap (BV)',
      nv: 'Naamloze vennootschap (NV)',
      vof: 'Vennootschap onder firma (VOF)',
      cv: 'Commanditaire vennootschap (CV)',
      maatschap: 'Maatschap',
      cooperatie_ua: 'Coöperatie (UA)',
      vereniging: 'Vereniging',
      stichting: 'Stichting',
      se_sce: 'Europese vennootschap (SE/SCE)',
      eenmanszaak: 'Eenmanszaak',
    },
    fields: {
      business_registration_number: 'KvK-nummer',
      tax_identification_number: 'RSIN',
      vat_number: 'Btw-nummer',
    },
  },
  be: {
    legalForms: {
      srl_bv: 'SRL / BV',
      sa_nv: 'SA / NV',
      sc_cv: 'SC / CV',
      vof: 'SNC / VOF',
      commv: 'SComm / CommV',
      maatschap: 'Société simple / Maatschap',
      asbl_vzw: 'ASBL / VZW',
      eenmanszaak: 'Entreprise individuelle / Eenmanszaak',
    },
    fields: {
      // BE derives an entity's tax number from its enterprise number - never
      // asked as its own field - so this mirrors what it's derived from
      // rather than a distinct native term.
      business_registration_number: "Numéro d'entreprise / Ondernemingsnummer",
      tax_identification_number: "Numéro d'entreprise / Ondernemingsnummer",
      vat_number: 'Numéro de TVA / Btw-nummer',
    },
  },
  de: {
    legalForms: {
      gbr: 'Gesellschaft bürgerlichen Rechts (GbR)',
      ohg: 'Offene Handelsgesellschaft (OHG)',
      kg: 'Kommanditgesellschaft (KG)',
      partg: 'Partnerschaftsgesellschaft (PartG)',
      gmbh: 'Gesellschaft mit beschränkter Haftung (GmbH)',
      ug: 'Unternehmergesellschaft (UG)',
      ag: 'Aktiengesellschaft (AG)',
      eg: 'Eingetragene Genossenschaft (eG)',
      kgaa: 'Kommanditgesellschaft auf Aktien (KGaA)',
      ev: 'Eingetragener Verein (e.V.)',
      stiftung: 'Stiftung',
      einzelunternehmen: 'Einzelunternehmen',
    },
    fields: {
      business_registration_number: 'Handelsregisternummer',
      tax_identification_number: 'Steuernummer',
      vat_number: 'USt-IdNr.',
    },
  },
  fr: {
    legalForms: {
      eurl_sarl: 'EURL / SARL',
      sasu_sas: 'SASU / SAS',
      sa: 'Société anonyme (SA)',
      snc: 'Société en nom collectif (SNC)',
      scs_sca: 'SCS / SCA',
      association_loi_1901: 'Association loi 1901',
      fondation: 'Fondation',
      sci_scp_sel: 'SCI / SCP / SEL',
      entrepreneur_individuel: 'Entrepreneur individuel',
    },
    fields: {
      // FR derives an entity's tax number from its SIREN - never asked as its
      // own field - so this mirrors what it's derived from.
      business_registration_number: 'SIREN',
      tax_identification_number: 'SIREN',
      vat_number: 'Numéro de TVA',
    },
  },
  gb: {
    legalForms: {
      partnership: 'Partnership',
      lp: 'Limited partnership (LP)',
      llp: 'Limited liability partnership (LLP)',
      ltd: 'Private limited company (Ltd)',
      overseas_company: 'Overseas company',
      sole_trader: 'Sole trader',
      plc: 'Public limited company (PLC)',
      company_limited_by_guarantee: 'Company limited by guarantee',
      cic_limited_by_shares:
        'Community interest company (CIC) limited by shares',
      cic_limited_by_guarantee:
        'Community interest company (CIC) limited by guarantee',
      cio: 'Charitable incorporated organisation (CIO)',
    },
    fields: {
      business_registration_number: 'Company number',
      tax_identification_number: 'UTR',
      vat_number: 'VAT number',
    },
  },
} as const;

export type Dac7SupportedCountry = keyof typeof dac7Labels;

export const isDac7SupportedCountry = (
  country: string,
): country is Dac7SupportedCountry => country in dac7Labels;
